import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Router } from '@angular/router';
import { Browser } from '@capacitor/browser';
import { NativeShazam } from 'src/app/plugins/native-runtime.plugins';
import { RequestService } from 'src/app/services/request/request.service';
import { AppNavigationStateService } from 'src/app/services/startup/app-navigation-state.service';

type RecognitionState = 'ready' | 'listening' | 'matched' | 'not-found' | 'unsupported' | 'error';

interface ShazamMatch {
  title: string;
  artist?: string;
  subtitle?: string;
  artworkUrl?: string;
  genres?: string[];
  isrc?: string;
  shazamUrl?: string;
  appleMusicUrl?: string;
  explicit?: boolean;
}

interface ShazamHistoryItem extends ShazamMatch {
  id?: number;
  identifiedAt?: string;
  pending?: boolean;
}

@Component({
  selector: 'app-shazam-page',
  templateUrl: './shazam-page.component.html',
  styleUrls: ['./shazam-page.component.scss'],
})
export class ShazamPageComponent implements OnInit, OnDestroy {
  state: RecognitionState = 'ready';
  match?: ShazamMatch;
  history: ShazamHistoryItem[] = [];
  historyLoading = false;
  historyMessage = '';
  pendingCount = 0;
  message = 'Acerca tu teléfono a la música y toca identificar.';
  private readonly pendingStorageKey = 'laniakea.shazam.pending.v1';
  private syncingPending = false;

  constructor(
    private router: Router,
    private request: RequestService,
    private navigationState: AppNavigationStateService,
  ) {}

  async ngOnInit(): Promise<void> {
    const autoIdentify = typeof history !== 'undefined' && history.state?.autoIdentify === true;
    if (autoIdentify) {
      history.replaceState({ ...history.state, autoIdentify: false }, '', location.href);
    }
    void this.initializeHistory();

    if (!this.supportsNativeRecognition()) {
      this.state = 'unsupported';
      this.message = 'La identificación con Shazam está disponible en la app de Laniakea para iPhone y Android.';
      return;
    }

    try {
      const result = await NativeShazam.isAvailable();
      if (!result.available) {
        this.state = 'unsupported';
        this.message = 'Shazam no está disponible en este dispositivo.';
      } else if (autoIdentify) {
        setTimeout(() => void this.identify(), 0);
      }
    } catch (error) {
      console.error('Native Shazam bridge could not be initialized.', error);
      this.state = 'unsupported';
      this.message = 'No pudimos iniciar Shazam en este dispositivo.';
    }
  }

  ngOnDestroy(): void {
    NativeShazam.stop().catch(() => undefined);
  }

  async identify(): Promise<void> {
    if (this.state === 'listening') return;

    this.state = 'listening';
    this.match = undefined;
    this.message = 'Escuchando la música a tu alrededor…';

    try {
      const developerToken = await this.getAndroidDeveloperToken();
      this.match = await NativeShazam.recognize(developerToken ? { developerToken } : undefined);
      this.state = 'matched';
    } catch (error: any) {
      const code = String(error?.code || '');
      console.error(
        `Native Shazam identification failed. code=${code || 'unknown'} platform=${Capacitor.getPlatform()} ${this.describeShazamError(error)}`,
      );
      if (code === 'SHAZAM_NO_MATCH' || code === 'SHAZAM_TIMEOUT') {
        this.state = 'not-found';
        this.message = 'No encontramos una coincidencia. Prueba más cerca de la fuente de audio.';
      } else if (code === 'SHAZAM_MICROPHONE_DENIED') {
        this.state = 'error';
        this.message = 'Activa el acceso al micrófono en Ajustes para identificar música.';
      } else if (code === 'SHAZAM_TOKEN_UNAVAILABLE') {
        this.state = 'error';
        this.message = 'No pudimos preparar Shazam. Revisa tu conexión e inténtalo de nuevo.';
      } else if (code === 'SHAZAM_AUDIO_FAILED') {
        this.state = 'error';
        this.message = 'No pudimos usar el micrófono de este dispositivo para identificar música.';
      } else {
        this.state = 'error';
        this.message = 'No pudimos identificar la canción. Revisa tu conexión e inténtalo de nuevo.';
      }
      return;
    }

    try {
      await this.saveIdentification(this.match);
      this.history = this.mergeHistory([this.match, ...this.history], this.readPendingIdentifications());
      this.message = 'Canción identificada y guardada.';
    } catch (error) {
      console.error('Unable to save Shazam identification.', error);
      this.queuePendingIdentification(this.match);
      this.message = 'Canción identificada; se guardará cuando vuelva la conexión.';
    }
  }

  async stop(): Promise<void> {
    await NativeShazam.stop().catch(() => undefined);
    this.state = 'ready';
    this.message = 'Identificación detenida.';
  }

  async openMatch(): Promise<void> {
    const url = this.match?.appleMusicUrl || this.match?.shazamUrl;
    if (url) await Browser.open({ url });
  }

  async openHistoryItem(item: ShazamHistoryItem): Promise<void> {
    const url = item.appleMusicUrl || item.shazamUrl;
    if (url) await Browser.open({ url });
  }

  private supportsNativeRecognition(): boolean {
    const platform = Capacitor.getPlatform();
    return platform === 'ios' || platform === 'android';
  }

  /**
   * iOS authenticates ShazamKit through its app entitlement. Android uses the
   * same Apple catalog, but requires a short-lived developer token signed by
   * Lambda. The private Media Services key never reaches the device.
   */
  private async getAndroidDeveloperToken(): Promise<string | undefined> {
    if (Capacitor.getPlatform() !== 'android') return undefined;

    try {
      const raw = await this.request.createRequestPost('shazam/token', { platform: 'android' });
      let response: any = raw;
      if (typeof response === 'string') {
        try { response = JSON.parse(response); } catch { response = {}; }
      }

      const token = response?.token || response?.developer_token || response?.developerToken;
      if (typeof token !== 'string' || !token.trim()) {
        throw new Error('The Android Shazam token response was empty.');
      }
      return token.trim();
    } catch (error) {
      // The token is short-lived and Android-only. Do not log its value.
      const requestError: any = error || {};
      const status = Number(requestError.status || requestError.statusCode || 0) || undefined;
      const message = this.describeShazamError(requestError);

      // Capacitor stringifies console object arguments as "[object Object]".
      // Keep this textual so Android logcat preserves the response details.
      console.error(`Android Shazam developer token could not be obtained. ${message}`);
      throw { code: 'SHAZAM_TOKEN_UNAVAILABLE', status, message };
    }
  }

  private async saveIdentification(match: ShazamMatch): Promise<void> {
    const raw = await this.request.createRequestPost('shazam/history', {
      title: match.title,
      artist: match.artist || null,
      subtitle: match.subtitle || null,
      artwork_url: match.artworkUrl || null,
      genres: match.genres || [],
      isrc: match.isrc || null,
      shazam_url: match.shazamUrl || null,
      apple_music_url: match.appleMusicUrl || null,
      explicit: Boolean(match.explicit),
    });
    const result = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (result?.ok !== true) {
      throw new Error(result?.message || 'The identification was not saved.');
    }
  }

  async refreshHistory(): Promise<void> {
    await this.flushPendingIdentifications();
    await this.loadHistory();
  }

  @HostListener('window:online')
  async onOnline(): Promise<void> {
    await this.flushPendingIdentifications();
    await this.loadHistory();
  }

  private async initializeHistory(): Promise<void> {
    await this.loadHistory();
    await this.flushPendingIdentifications();
    if (this.pendingCount === 0) await this.loadHistory();
  }

  private async loadHistory(): Promise<void> {
    this.historyLoading = true;
    try {
      const raw = await this.request.createRequestGet('shazam/history', { limit: 50 });
      const result = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const serverItems = Array.isArray(result?.items) ? result.items : [];
      this.history = this.mergeHistory(serverItems, this.readPendingIdentifications());
      this.historyMessage = this.history.length ? '' : 'Tus canciones identificadas aparecerán aquí.';
    } catch (error) {
      const sessionMissing = this.isMissingNativeSession(error);
      if (sessionMissing) {
        console.info('Shazam history is waiting for the native user session.');
      } else {
        console.error(`Unable to load Shazam history. ${this.describeShazamError(error)}`);
      }
      this.history = this.mergeHistory([], this.readPendingIdentifications());
      this.historyMessage = this.history.length
        ? 'Mostrando canciones pendientes de sincronizar.'
        : sessionMissing
          ? 'Inicia sesión para sincronizar tu historial de Shazam.'
          : 'No pudimos cargar el historial. Inténtalo de nuevo.';
    } finally {
      this.pendingCount = this.readPendingIdentifications().length;
      this.historyLoading = false;
    }
  }

  /** Formats native HTTP errors safely for Android logcat without leaking tokens. */
  private describeShazamError(error: any): string {
    const requestError = error || {};
    const status = Number(requestError.status || requestError.statusCode || 0);
    const errorBody = requestError.error || requestError.body || requestError.data;
    const code = String(requestError.code || errorBody?.code || '').trim();
    let message = typeof errorBody === 'string'
      ? errorBody
      : errorBody?.message || requestError.message || '';

    if (!message && errorBody && typeof errorBody === 'object') {
      try {
        message = JSON.stringify(errorBody);
      } catch {
        message = '';
      }
    }

    return [
      status ? `status=${status}` : '',
      code ? `errorCode=${code}` : '',
      message ? `message=${String(message).slice(0, 300)}` : 'message=No error response was provided',
    ].filter(Boolean).join(' ');
  }

  private isMissingNativeSession(error: any): boolean {
    return Capacitor.getPlatform() !== 'web' && Number(error?.code) === 2;
  }

  private async flushPendingIdentifications(): Promise<void> {
    if (this.syncingPending || !this.readPendingIdentifications().length) return;
    this.syncingPending = true;
    try {
      const remaining: ShazamMatch[] = [];
      for (const item of this.readPendingIdentifications()) {
        try {
          await this.saveIdentification(item);
        } catch (error) {
          console.error('Unable to sync pending Shazam identification.', error);
          remaining.push(item);
        }
      }
      this.writePendingIdentifications(remaining);
      this.pendingCount = remaining.length;
    } finally {
      this.syncingPending = false;
    }
  }

  private queuePendingIdentification(match: ShazamMatch): void {
    const pending = this.readPendingIdentifications();
    if (!pending.some(item => this.historyKey(item) === this.historyKey(match))) {
      pending.unshift(match);
      this.writePendingIdentifications(pending.slice(0, 50));
    }
    this.pendingCount = this.readPendingIdentifications().length;
    this.history = this.mergeHistory(this.history, this.readPendingIdentifications());
  }

  private readPendingIdentifications(): ShazamMatch[] {
    try {
      const raw = localStorage.getItem(this.pendingStorageKey);
      const value = raw ? JSON.parse(raw) : [];
      return Array.isArray(value) ? value : [];
    } catch {
      return [];
    }
  }

  private writePendingIdentifications(items: ShazamMatch[]): void {
    try {
      localStorage.setItem(this.pendingStorageKey, JSON.stringify(items));
    } catch (error) {
      console.error('Unable to persist pending Shazam identifications.', error);
    }
  }

  private mergeHistory(serverItems: ShazamHistoryItem[], pending: ShazamMatch[]): ShazamHistoryItem[] {
    const result: ShazamHistoryItem[] = [];
    for (const item of [...serverItems, ...pending.map(value => ({ ...value, pending: true }))]) {
      if (!item?.title || result.some(existing => this.historyKey(existing) === this.historyKey(item))) continue;
      result.push(item);
    }
    return result.slice(0, 50);
  }

  private historyKey(item: ShazamMatch): string {
    return (item.isrc || `${item.title}|${item.artist || item.subtitle || ''}`).trim().toLowerCase();
  }

  async back(): Promise<void> {
    // A native process restart can leave Ionic without a history entry even
    // though this page was restored successfully. The shared navigation state
    // supplies the prior Laniakea page (or the main interface) in that case.
    const fallback = this.navigationState.backFallbackForCurrentRoute(this.router.url);
    const didNavigate = await this.router.navigateByUrl(fallback, { replaceUrl: true });
    if (!didNavigate) {
      await this.router.navigateByUrl('/home/ecosystem-page', { replaceUrl: true });
    }
  }
}
